import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { Barcode } from '../components/Barcode.tsx'
import { SendToChatDialog } from '../components/SendToChatDialog.tsx'
import { guessFormat, type BarcodeFormat } from '../lib/barcode.ts'
import { dateValue, fromInputs, timeValue } from '../lib/planner.ts'

/**
 * КОШЕЛЁК
 *
 * Билеты и дисконтные карты. Ищут их одинаково — стоя в очереди, одной
 * рукой, за десять секунд, — поэтому и лежат они рядом.
 *
 * Ближайший билет показывается первым и крупно: в девяти случаях
 * из десяти открывают именно его.
 */

type Ticket = {
  id: string
  kind: string
  title: string
  venue: string | null
  startsAt: string | null
  endsAt: string | null
  seat: string | null
  bookingRef: string | null
  barcodeData: string | null
  barcodeFormat: BarcodeFormat
  price: number | null
  note: string | null
  used: boolean
  past: boolean
  owner: { id: string; displayName: string; avatarUrl: string | null }
  mine: boolean
  eventId: string | null
  mediaItemId: string | null
}

type Card = {
  id: string
  merchantName: string
  cardNumber: string | null
  barcodeData: string | null
  barcodeFormat: BarcodeFormat
  color: string | null
  note: string | null
  isFamilyWide: boolean
  expiresOn: string | null
  expired: boolean
  owner: { id: string; displayName: string; avatarUrl: string | null }
  mine: boolean
}

const TICKET_KINDS: Record<string, string> = {
  CINEMA: 'Кино',
  CONCERT: 'Концерт',
  THEATRE: 'Театр',
  TRAIN: 'Поезд',
  FLIGHT: 'Самолёт',
  BUS: 'Автобус',
  MUSEUM: 'Музей',
  SPORT: 'Спорт',
  OTHER: 'Другое',
}

const TICKET_ICONS: Record<string, string> = {
  CINEMA: '🎬',
  CONCERT: '🎸',
  THEATRE: '🎭',
  TRAIN: '🚆',
  FLIGHT: '✈️',
  BUS: '🚌',
  MUSEUM: '🏛',
  SPORT: '⚽',
  OTHER: '🎫',
}

const whenText = (iso: string | null): string => {
  if (!iso) return ''
  const date = new Date(iso)
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function WalletPage() {
  const { ticketId } = useParams<{ ticketId?: string }>()
  const navigate = useNavigate()

  const [tab, setTab] = useState<'tickets' | 'cards'>('tickets')
  const [tickets, setTickets] = useState<Ticket[] | null>(null)
  const [cards, setCards] = useState<Card[] | null>(null)
  const [ticketDraft, setTicketDraft] = useState<Partial<Ticket> | null>(null)
  const [cardDraft, setCardDraft] = useState<Partial<Card> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ tickets: Ticket[] }>('/api/wallet/tickets')
      .then((data) => setTickets(data.tickets))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))

    api<{ cards: Card[] }>('/api/wallet/cards')
      .then((data) => setCards(data.cards))
      .catch(() => setCards([]))
  }

  useEffect(load, [])

  // Переход по ссылке из переписки: /wallet/ticket/<id>
  const open = ticketId ? tickets?.find((t) => t.id === ticketId) : null

  const upcoming = (tickets ?? []).filter((t) => !t.past)
  const past = (tickets ?? []).filter((t) => t.past)

  return (
    <div className="section">
      <header className="section-head">
        <h1>Кошелёк</h1>
        <button
          className="icon-btn"
          onClick={() =>
            tab === 'tickets'
              ? setTicketDraft({ kind: 'OTHER', title: '' })
              : setCardDraft({ merchantName: '' })
          }
          aria-label="Добавить"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
          </svg>
        </button>
      </header>

      <div className="planner-tabs">
        <button
          className={'tab-btn' + (tab === 'tickets' ? ' is-active' : '')}
          onClick={() => setTab('tickets')}
        >
          Билеты{upcoming.length > 0 && ` · ${upcoming.length}`}
        </button>
        <button
          className={'tab-btn' + (tab === 'cards' ? ' is-active' : '')}
          onClick={() => setTab('cards')}
        >
          Карты
        </button>
      </div>

      <div className="section-body scroll">
        {tab === 'tickets' ? (
          <>
            {tickets?.length === 0 && (
              <p className="empty-hint">
                Билетов нет. Сюда складывают билеты в кино, на поезд, на концерт —
                чтобы на контроле не искать их в почте.
              </p>
            )}

            {upcoming.map((ticket) => (
              <TicketRow
                key={ticket.id}
                ticket={ticket}
                onOpen={() => navigate(`/wallet/ticket/${ticket.id}`)}
              />
            ))}

            {past.length > 0 && (
              <>
                <div className="done-head">
                  <span>Прошедшие · {past.length}</span>
                </div>
                {past.map((ticket) => (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    onOpen={() => navigate(`/wallet/ticket/${ticket.id}`)}
                  />
                ))}
              </>
            )}
          </>
        ) : (
          <>
            {cards?.length === 0 && (
              <p className="empty-hint">
                Карт нет. Добавь карту магазина или аптеки — на кассе достаточно
                будет открыть её здесь.
              </p>
            )}

            <div className="card-grid">
              {cards?.map((card) => (
                <button
                  key={card.id}
                  className={'loyalty-card' + (card.expired ? ' is-expired' : '')}
                  style={card.color ? { borderColor: card.color } : undefined}
                  onClick={() => setCardDraft(card)}
                >
                  <span className="loyalty-name">{card.merchantName}</span>
                  {card.cardNumber && <span className="loyalty-number">{card.cardNumber}</span>}
                  <span className="loyalty-sub">
                    {card.isFamilyWide ? 'общая' : 'моя'}
                    {card.expired && ' · срок истёк'}
                    {!card.mine && ` · ${card.owner.displayName}`}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {error && <div className="sheet-error">{error}</div>}
      </div>

      {open && (
        <TicketSheet
          ticket={open}
          onClose={() => navigate('/wallet')}
          onEdit={() => {
            setTicketDraft(open)
            navigate('/wallet')
          }}
          onChanged={load}
        />
      )}

      {ticketDraft && (
        <TicketSheetEdit
          draft={ticketDraft}
          onClose={() => setTicketDraft(null)}
          onSaved={load}
        />
      )}

      {cardDraft && (
        <CardSheet draft={cardDraft} onClose={() => setCardDraft(null)} onSaved={load} />
      )}
    </div>
  )
}

function TicketRow({ ticket, onOpen }: { ticket: Ticket; onOpen: () => void }) {
  return (
    <button className={'ticket-row' + (ticket.past ? ' is-past' : '')} onClick={onOpen}>
      <span className="ticket-icon" aria-hidden="true">
        {TICKET_ICONS[ticket.kind] ?? '🎫'}
      </span>

      <span className="ticket-text">
        <span className="ticket-title">{ticket.title}</span>
        <span className="ticket-sub">
          {ticket.startsAt && <span>{whenText(ticket.startsAt)}</span>}
          {ticket.venue && <span>{ticket.venue}</span>}
          {ticket.seat && <span>{ticket.seat}</span>}
        </span>
      </span>

      {!ticket.mine && (
        <Avatar
          id={ticket.owner.id}
          name={ticket.owner.displayName}
          url={ticket.owner.avatarUrl}
          size={22}
        />
      )}
    </button>
  )
}

/** Сам билет: то, что показывают на контроле. */
function TicketSheet({
  ticket,
  onClose,
  onEdit,
  onChanged,
}: {
  ticket: Ticket
  onClose: () => void
  onEdit: () => void
  onChanged: () => void
}) {
  const [sending, setSending] = useState(false)

  const markUsed = async () => {
    await api(`/api/wallet/tickets/${ticket.id}`, {
      method: 'PATCH',
      body: { used: !ticket.used },
    })
    onChanged()
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{ticket.title}</h2>
          <button className="icon-btn" onClick={() => setSending(true)} title="Отправить в переписку">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
            </svg>
          </button>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <p className="sheet-text">
          {TICKET_KINDS[ticket.kind] ?? 'Билет'}
          {ticket.startsAt && ` · ${whenText(ticket.startsAt)}`}
        </p>

        {ticket.venue && <p className="sheet-text">{ticket.venue}</p>}

        <div className="ticket-facts">
          {ticket.seat && (
            <span className="ticket-fact">
              <span className="ticket-fact-name">Место</span>
              <span className="ticket-fact-value">{ticket.seat}</span>
            </span>
          )}
          {ticket.bookingRef && (
            <span className="ticket-fact">
              <span className="ticket-fact-name">Бронь</span>
              <span className="ticket-fact-value">{ticket.bookingRef}</span>
            </span>
          )}
          {ticket.price !== null && (
            <span className="ticket-fact">
              <span className="ticket-fact-name">Цена</span>
              <span className="ticket-fact-value">
                {ticket.price.toLocaleString('ru-RU')} ₽
              </span>
            </span>
          )}
        </div>

        {ticket.barcodeData && (
          <Barcode value={ticket.barcodeData} format={ticket.barcodeFormat} height={110} />
        )}

        {ticket.note && <p className="sheet-note">{ticket.note}</p>}

        <div className="sheet-actions">
          <button className="btn-primary" onClick={() => void markUsed()}>
            {ticket.used ? 'Вернуть в актуальные' : 'Использован'}
          </button>
          {ticket.mine && (
            <button className="btn-quiet" onClick={onEdit}>
              Изменить
            </button>
          )}
        </div>

        {sending && (
          <SendToChatDialog
            entityType="TICKET"
            entityId={ticket.id}
            title={ticket.title}
            onClose={() => setSending(false)}
          />
        )}
      </div>
    </div>
  )
}

function TicketSheetEdit({
  draft,
  onClose,
  onSaved,
}: {
  draft: Partial<Ticket>
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)
  const starts = draft.startsAt ? new Date(draft.startsAt) : null

  const [kind, setKind] = useState(draft.kind ?? 'OTHER')
  const [title, setTitle] = useState(draft.title ?? '')
  const [venue, setVenue] = useState(draft.venue ?? '')
  const [date, setDate] = useState(starts ? dateValue(starts) : '')
  const [time, setTime] = useState(starts ? timeValue(starts) : '')
  const [seat, setSeat] = useState(draft.seat ?? '')
  const [bookingRef, setBookingRef] = useState(draft.bookingRef ?? '')
  const [barcodeData, setBarcodeData] = useState(draft.barcodeData ?? '')
  const [barcodeFormat, setBarcodeFormat] = useState<BarcodeFormat>(
    draft.barcodeFormat ?? 'NONE',
  )
  const [price, setPrice] = useState(draft.price?.toString() ?? '')
  const [note, setNote] = useState(draft.note ?? '')
  const [eventId, setEventId] = useState(draft.eventId ?? '')
  const [mediaItemId, setMediaItemId] = useState(draft.mediaItemId ?? '')

  const [events, setEvents] = useState<{ id: string; title: string; startsAt: string }[]>([])
  const [movies, setMovies] = useState<{ id: string; title: string }[]>([])

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * К чему билет относится: к поездке в планере и к фильму из списка
   * «посмотреть». Без этого раздел превращается в свалку картинок
   * со штрихкодами — через полгода не вспомнить, что это был за поход.
   */
  useEffect(() => {
    const from = new Date()
    from.setMonth(from.getMonth() - 1)
    const to = new Date()
    to.setMonth(to.getMonth() + 6)

    api<{ events: { id: string; title: string; startsAt: string }[] }>(
      `/api/planner/agenda?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
    )
      .then((data) => {
        // Одно и то же повторяющееся событие в списке не дублируем
        const seen = new Set<string>()
        setEvents(
          data.events.filter((event) => {
            if (seen.has(event.id)) return false
            seen.add(event.id)
            return true
          }),
        )
      })
      .catch(() => setEvents([]))

    api<{ items: { id: string; title: string; status: string }[] }>('/api/media')
      .then((data) => setMovies(data.items.filter((item) => item.status !== 'WATCHED')))
      .catch(() => setMovies([]))
  }, [])

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const body = {
        kind,
        title: title.trim(),
        venue: venue.trim() || null,
        startsAt: date ? fromInputs(date, time || null).toISOString() : null,
        seat: seat.trim() || null,
        bookingRef: bookingRef.trim() || null,
        barcodeData: barcodeData.trim() || null,
        // Формат обычно неизвестен человеку — угадываем по самому номеру
        barcodeFormat: barcodeData.trim()
          ? barcodeFormat === 'NONE'
            ? guessFormat(barcodeData.trim())
            : barcodeFormat
          : 'NONE',
        price: price.trim() ? Number(price.replace(',', '.')) : null,
        note: note.trim() || null,
        eventId: eventId || null,
        mediaItemId: mediaItemId || null,
      }

      if (draft.id) {
        await api(`/api/wallet/tickets/${draft.id}`, { method: 'PATCH', body })
      } else {
        await api('/api/wallet/tickets', { method: 'POST', body })
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!draft.id || !window.confirm('Удалить билет?')) return
    setBusy(true)
    try {
      await api(`/api/wallet/tickets/${draft.id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? 'Билет' : 'Новый билет'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <label className="field">
          <span className="field-label">Что за билет</span>
          <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(TICKET_KINDS).map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field-label">Куда</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Название фильма, рейс, спектакль"
            maxLength={200}
            autoFocus={!editing}
          />
        </label>

        <label className="field">
          <span className="field-label">Где</span>
          <input
            className="input"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            placeholder="Кинотеатр, вокзал, зал"
            maxLength={200}
          />
        </label>

        <div className="amount-row">
          <label className="field">
            <span className="field-label">Когда</span>
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Во сколько</span>
            <input
              className="input"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={!date}
            />
          </label>
        </div>

        <div className="amount-row">
          <label className="field">
            <span className="field-label">Место</span>
            <input
              className="input"
              value={seat}
              onChange={(e) => setSeat(e.target.value)}
              placeholder="Ряд 5, место 12"
              maxLength={100}
            />
          </label>
          <label className="field">
            <span className="field-label">Номер брони</span>
            <input
              className="input"
              value={bookingRef}
              onChange={(e) => setBookingRef(e.target.value)}
              maxLength={100}
            />
          </label>
        </div>

        <label className="field">
          <span className="field-label">Штрихкод или номер для контроля</span>
          <input
            className="input"
            value={barcodeData}
            onChange={(e) => setBarcodeData(e.target.value)}
            placeholder="То, что сканируют на входе"
            maxLength={4000}
          />
        </label>

        {barcodeData.trim() && (
          <label className="field">
            <span className="field-label">Вид кода</span>
            <select
              className="input"
              value={barcodeFormat}
              onChange={(e) => setBarcodeFormat(e.target.value as BarcodeFormat)}
            >
              <option value="NONE">Определить самому</option>
              <option value="QR">QR</option>
              <option value="CODE128">Полосатый (CODE128)</option>
              <option value="EAN13">Полосатый (EAN-13)</option>
              <option value="CODE39">Полосатый (CODE39)</option>
            </select>
          </label>
        )}

        <div className="amount-row">
          <label className="field">
            <span className="field-label">Цена</span>
            <input
              className="input"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Не обязательно"
              inputMode="decimal"
            />
          </label>
        </div>

        {events.length > 0 && (
          <label className="field">
            <span className="field-label">К какому событию в планере</span>
            <select className="input" value={eventId} onChange={(e) => setEventId(e.target.value)}>
              <option value="">Ни к какому</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {new Date(event.startsAt).toLocaleDateString('ru-RU')} — {event.title}
                </option>
              ))}
            </select>
          </label>
        )}

        {kind === 'CINEMA' && movies.length > 0 && (
          <label className="field">
            <span className="field-label">На какой фильм</span>
            <select
              className="input"
              value={mediaItemId}
              onChange={(e) => setMediaItemId(e.target.value)}
            >
              <option value="">Не из списка</option>
              {movies.map((movie) => (
                <option key={movie.id} value={movie.id}>
                  {movie.title}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="field">
          <span className="field-label">Заметка</span>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
          />
        </label>

        {error && <div className="sheet-error">{error}</div>}

        <div className="sheet-actions">
          <button className="btn-primary" disabled={busy || !title.trim()} onClick={() => void save()}>
            {busy ? '…' : 'Сохранить'}
          </button>
          {editing && (
            <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
              Удалить
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function CardSheet({
  draft,
  onClose,
  onSaved,
}: {
  draft: Partial<Card>
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)
  const readOnly = editing && draft.mine === false

  const [merchantName, setMerchantName] = useState(draft.merchantName ?? '')
  const [cardNumber, setCardNumber] = useState(draft.cardNumber ?? '')
  const [barcodeFormat, setBarcodeFormat] = useState<BarcodeFormat>(
    draft.barcodeFormat ?? 'NONE',
  )
  const [isFamilyWide, setIsFamilyWide] = useState(draft.isFamilyWide ?? false)
  const [note, setNote] = useState(draft.note ?? '')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const code = (draft.barcodeData ?? cardNumber).trim()
  const format = barcodeFormat === 'NONE' && code ? guessFormat(code) : barcodeFormat

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const number = cardNumber.trim()
      const body = {
        merchantName: merchantName.trim(),
        cardNumber: number || null,
        // Номер карты и есть то, что сканируют: отдельного поля
        // для штрихкода человеку не нужно
        barcodeData: number || null,
        barcodeFormat: number ? (barcodeFormat === 'NONE' ? guessFormat(number) : barcodeFormat) : 'NONE',
        isFamilyWide,
        note: note.trim() || null,
      }

      if (draft.id) {
        await api(`/api/wallet/cards/${draft.id}`, { method: 'PATCH', body })
      } else {
        await api('/api/wallet/cards', { method: 'POST', body })
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!draft.id || !window.confirm('Удалить карту?')) return
    setBusy(true)
    try {
      await api(`/api/wallet/cards/${draft.id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? merchantName || 'Карта' : 'Новая карта'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {/* Открыл карту — сразу видишь код: за этим её и открывают */}
        {code && <Barcode value={code} format={format} />}

        {readOnly ? (
          <>
            <p className="sheet-text">Карта {draft.owner?.displayName}</p>
            {note && <p className="sheet-note">{note}</p>}
          </>
        ) : (
          <>
            <label className="field">
              <span className="field-label">Магазин</span>
              <input
                className="input"
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
                placeholder="Пятёрочка, аптека, заправка"
                maxLength={120}
                autoFocus={!editing}
              />
            </label>

            <label className="field">
              <span className="field-label">Номер карты</span>
              <input
                className="input"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="Цифры под штрихкодом"
                maxLength={100}
                inputMode="numeric"
              />
            </label>

            {cardNumber.trim() && (
              <label className="field">
                <span className="field-label">Вид кода</span>
                <select
                  className="input"
                  value={barcodeFormat}
                  onChange={(e) => setBarcodeFormat(e.target.value as BarcodeFormat)}
                >
                  <option value="NONE">Определить самому</option>
                  <option value="EAN13">Полосатый (EAN-13)</option>
                  <option value="CODE128">Полосатый (CODE128)</option>
                  <option value="CODE39">Полосатый (CODE39)</option>
                  <option value="QR">QR</option>
                </select>
              </label>
            )}

            <label className="check-row">
              <input
                type="checkbox"
                checked={isFamilyWide}
                onChange={(e) => setIsFamilyWide(e.target.checked)}
              />
              <span>Общая — видна всей семье</span>
            </label>
            <p className="sheet-note">
              Пока карта не общая, её видишь только ты.
            </p>

            <label className="field">
              <span className="field-label">Заметка</span>
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
              />
            </label>
          </>
        )}

        {error && <div className="sheet-error">{error}</div>}

        {!readOnly && (
          <div className="sheet-actions">
            <button
              className="btn-primary"
              disabled={busy || !merchantName.trim()}
              onClick={() => void save()}
            >
              {busy ? '…' : 'Сохранить'}
            </button>
            {editing && (
              <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
                Удалить
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
